import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, User, LogOut, Gift, ChevronDown, Building2, Store, LayoutDashboard, Sparkles, Trophy, Wallet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { flag } from '../config';
import { api, getToken } from '../lib/api';
import { countActiveClaims } from '../lib/claimStatus';
import MobileNav from './navigation/MobileNav';
import AudienceNav, { DEFAULT_AUDIENCE_ITEMS } from './public/AudienceNav';
import { headerMenuFlags } from '../lib/roles';

/**
 * The pre-redesign site header, kept byte-for-byte for SLOT_MODEL off (flags, not deletion,
 * ADR-0009). components/Header.jsx renders it when the flag is off and the redesigned
 * SiteHeader (components/site/header) when it is on, so the `flag('SLOT_MODEL')` branches
 * below are only reached if this component is rendered directly. Remove at cutover cleanup.
 */
const HeaderLegacy = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showRewardsMenu, setShowRewardsMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [claimsCount, setClaimsCount] = useState(0);
  const [tapsBalance, setTapsBalance] = useState(0);
  const mobileRewardsRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, openAuthModal } = useAuth();

  // Which dashboard sections the user menu shows (role logic: lib/roles.js)
  const { isAdmin, isAdvertiser, isVenuePartner } = headerMenuFlags(user);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile rewards menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileRewardsRef.current && !mobileRewardsRef.current.contains(event.target)) {
        setShowRewardsMenu(false);
      }
    };
    
    if (showRewardsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showRewardsMenu]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showUserMenu]);

  // Close all menus on route change
  useEffect(() => {
    setShowUserMenu(false);
    setShowRewardsMenu(false);
  }, [location.pathname]);

  // Reset menu state when authentication status changes
  useEffect(() => {
    setShowUserMenu(false);
    setShowRewardsMenu(false);
  }, [isAuthenticated]);

  // Fetch user's claims count and taps balance when authenticated
  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated || !user?.id) {
        setClaimsCount(0);
        setTapsBalance(0);
        return;
      }
      
      if (!getToken()) {
        setClaimsCount(0);
        setTapsBalance(0);
        return;
      }

      // Fetch claims count. A 401 here is expected while the session is still
      // settling, so a failure falls back to zero instead of logging.
      try {
        // Active codes only: my-claims also returns redeemed claims since Phase 6b.
        const { data: claims } = await api.get('/deals/my-claims');
        setClaimsCount(countActiveClaims(claims));
      } catch {
        setClaimsCount(0);
      }

      // Fetch taps balance (Phase 1: only when REWARDS is on)
      if (!flag('REWARDS')) {
        setTapsBalance(0);
        return;
      }
      try {
        const { data: tapsData } = await api.get('/taps/balance');
        setTapsBalance(tapsData.total_taps || 0);
      } catch {
        setTapsBalance(0);
      }
    };
    
    fetchUserData();
  }, [isAuthenticated, user?.id]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const isActive = (href) => location.pathname === href;

  // Hide navigation in admin area
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <>
      {/* Sticky Floating Navigation */}
      <header className="fixed top-0 z-50 pointer-events-none" style={{ left: '50%', transform: 'translateX(-50%)', width: '100%' }}>
        <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '90rem' }}>
          <motion.div
            className="mt-6 mx-auto rounded-full shadow-lg pointer-events-auto"
            style={{
              maxWidth: '90rem',
              backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 122, 26, 0.1)',
            }}
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div 
              className="flex items-center justify-between"
              style={{
                padding: scrolled ? '10px 24px' : '14px 32px',
                transition: 'padding 0.3s ease'
              }}
            >
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <motion.img 
                  src="/tappd-logo.png" 
                  alt="Tappd"
                  style={{
                    height: scrolled ? '45px' : '50px',
                    width: 'auto',
                    objectFit: 'contain',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                />
              </Link>

              {/* Desktop Navigation - Center. SLOT_MODEL (Phase 5c): navigation by audience,
                  Deals · For Businesses · For Venues · Pricing; the old items stay for flag-off. */}
              {flag('SLOT_MODEL') ? (
                <AudienceNav
                  items={DEFAULT_AUDIENCE_ITEMS}
                  label="Main"
                  className="hidden md:block md:w-auto"
                />
              ) : (
              <nav className="hidden md:flex items-center gap-1">
                {/* How It Works */}
                <Link to="/how-it-works">
                  <motion.div
                    className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap font-body"
                    style={{
                      color: isActive('/how-it-works') ? '#FFFFFF' : '#475569',
                      backgroundColor: isActive('/how-it-works') ? '#FF7A1A' : 'transparent',
                    }}
                    whileHover={{ backgroundColor: isActive('/how-it-works') ? '#FF7A1A' : '#F3F4F6', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    How It Works
                  </motion.div>
                </Link>

                {/* Advertise */}
                <Link to="/for-businesses">
                  <motion.div
                    className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap font-body"
                    style={{
                      color: isActive('/for-businesses') ? '#FFFFFF' : '#475569',
                      backgroundColor: isActive('/for-businesses') ? '#6F5BEB' : 'transparent',
                    }}
                    whileHover={{ backgroundColor: isActive('/for-businesses') ? '#6F5BEB' : '#F3F4F6', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Advertise
                  </motion.div>
                </Link>

                {/* Pricing */}
                <Link to="/pricing">
                  <motion.div
                    className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap font-body"
                    style={{
                      color: isActive('/pricing') ? '#FFFFFF' : '#475569',
                      backgroundColor: isActive('/pricing') ? '#6F5BEB' : 'transparent',
                    }}
                    whileHover={{ backgroundColor: isActive('/pricing') ? '#6F5BEB' : '#F3F4F6', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Pricing
                  </motion.div>
                </Link>

                {/* Free Coasters */}
                <Link to="/for-venues">
                  <motion.div
                    className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap font-body"
                    style={{
                      color: isActive('/for-venues') ? '#FFFFFF' : '#475569',
                      backgroundColor: isActive('/for-venues') ? '#6F5BEB' : 'transparent',
                    }}
                    whileHover={{ backgroundColor: isActive('/for-venues') ? '#6F5BEB' : '#F3F4F6', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Free Coasters
                  </motion.div>
                </Link>

                {/* Founder's Story */}
                <Link to="/our-story">
                  <motion.div
                    className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap font-body"
                    style={{
                      color: isActive('/our-story') ? '#FFFFFF' : '#475569',
                      backgroundColor: isActive('/our-story') ? '#6F5BEB' : 'transparent',
                    }}
                    whileHover={{ backgroundColor: isActive('/our-story') ? '#6F5BEB' : '#F3F4F6', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Founder's Story
                  </motion.div>
                </Link>

                {/* Visual Separator */}
                <div className="w-px h-6 bg-gray-300 mx-2"></div>

                {/* Deals Dropdown - Orange Pill */}
                <div 
                  className="relative"
                  onMouseEnter={() => setShowRewardsMenu(true)}
                  onMouseLeave={() => setShowRewardsMenu(false)}
                >
                  <motion.button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={showRewardsMenu}
                    onClick={() => setShowRewardsMenu((open) => !open)}
                    onFocus={() => setShowRewardsMenu(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setShowRewardsMenu(false); }}
                    className="px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold whitespace-nowrap font-body flex items-center gap-1.5"
                    style={{
                      color: (isActive('/local-deals-near-me') || isActive('/deal-wallet') || isActive('/rewards')) ? '#FFFFFF' : '#FF7A1A',
                      backgroundColor: (isActive('/local-deals-near-me') || isActive('/deal-wallet') || isActive('/rewards')) ? '#FF7A1A' : 'transparent',
                      border: (isActive('/local-deals-near-me') || isActive('/deal-wallet') || isActive('/rewards')) ? 'none' : '2px solid #FF7A1A',
                    }}
                    whileHover={{ backgroundColor: '#FF7A1A', color: '#FFFFFF', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Gift size={16} aria-hidden="true" />
                    Deals
                    <ChevronDown size={14} aria-hidden="true" />
                  </motion.button>
                  
                  <AnimatePresence>
                    {showRewardsMenu && (
                      <motion.div 
                        className="absolute right-0 mt-2 w-56 rounded-2xl shadow-2xl overflow-hidden"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #FFE4CC' }}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {/* Deals Near You */}
                        <Link 
                          to="/local-deals-near-me" 
                          onClick={() => setShowRewardsMenu(false)} 
                          className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors"
                        >
                          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                            <span className="text-sm">🎁</span>
                          </div>
                          <div>
                            <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deals Near You</span>
                            <p className="text-xs text-gray-500">Find local offers</p>
                          </div>
                        </Link>
                        
                        {/* Deal Wallet - different behavior based on auth */}
                        {isAuthenticated ? (
                          <Link 
                            to="/deal-wallet" 
                            onClick={() => setShowRewardsMenu(false)} 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center">
                              <Wallet size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deal Wallet</span>
                              <p className="text-xs text-gray-500">Your saved deals</p>
                            </div>
                            {claimsCount > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                                {claimsCount}
                              </span>
                            )}
                          </Link>
                        ) : (
                          <button 
                            onClick={() => {
                              setShowRewardsMenu(false);
                              openAuthModal('login');
                            }} 
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors text-left"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center">
                              <Wallet size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deal Wallet</span>
                              <p className="text-xs text-gray-500">Sign in to view</p>
                            </div>
                          </button>
                        )}
                        
                        {/* Taps Rewards - NEW */}
                        {flag('REWARDS') && (isAuthenticated ? (
                          <Link 
                            to="/rewards" 
                            onClick={() => setShowRewardsMenu(false)} 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                              <Trophy size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Taps Rewards</span>
                              <p className="text-xs text-gray-500">Earn & redeem points</p>
                            </div>
                            {tapsBalance > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-purple-500 text-white text-xs font-bold rounded-full">
                                {tapsBalance}
                              </span>
                            )}
                          </Link>
                        ) : (
                          <button 
                            onClick={() => {
                              setShowRewardsMenu(false);
                              openAuthModal('login');
                            }} 
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors text-left"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                              <Trophy size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Taps Rewards</span>
                              <p className="text-xs text-gray-500">Sign in to earn</p>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </nav>
              )}

              {/* Desktop Right Section - Auth */}
              <div className="hidden md:flex items-center gap-3">
                {!isAuthenticated ? (
                  <>
                    <motion.button
                      onClick={() => openAuthModal('login')}
                      className="px-4 py-2 rounded-full text-sm font-semibold font-body"
                      style={{
                        color: '#475569',
                        backgroundColor: 'transparent',
                        border: '2px solid #E5E7EB'
                      }}
                      whileHover={{ borderColor: '#6F5BEB', color: '#6F5BEB', scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Log In
                    </motion.button>
                    <motion.button
                      onClick={() => openAuthModal('signup')}
                      className="px-5 py-2 rounded-full text-sm font-bold font-body"
                      style={{
                        background: 'linear-gradient(135deg, #FF7A1A 0%, #F59E0B 100%)',
                        color: '#FFFFFF',
                        border: 'none'
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Sign Up
                    </motion.button>
                  </>
                ) : (
                  <div
                    ref={userMenuRef}
                    className="relative"
                    onMouseEnter={() => setShowUserMenu(true)}
                    onMouseLeave={() => setShowUserMenu(false)}
                  >
                    <motion.button
                      className="flex items-center gap-2 px-3 py-2 rounded-full"
                      style={{
                        backgroundColor: '#FFF6EA',
                        border: '2px solid #FF7A1A'
                      }}
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <User size={16} style={{ color: '#FF7A1A' }} />
                      <span className="text-sm font-semibold font-body" style={{ color: '#111827' }}>
                        Hi, {user?.first_name || 'User'}
                      </span>
                      <ChevronDown size={14} style={{ color: '#6B7280' }} />
                    </motion.button>
                    
                    <AnimatePresence>
                      {showUserMenu && (
                        <motion.div 
                          className="absolute right-0 mt-2 w-52 rounded-2xl shadow-2xl overflow-hidden"
                          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                        >
                          <Link to="/profile" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                            <User size={18} style={{ color: '#6F5BEB' }} />
                            <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>My Profile</span>
                          </Link>
                          
                          {/* Admin Section */}
                          {isAdmin && (
                            <>
                              <div className="px-4 py-2 border-t border-gray-100">
                                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Admin</span>
                              </div>
                              <Link to="/admin/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors">
                                <LayoutDashboard size={18} style={{ color: '#6F5BEB' }} />
                                <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Master Dashboard</span>
                              </Link>
                            </>
                          )}
                          
                          {/* Business Advertiser Section */}
                          {isAdvertiser && (
                            <>
                              <div className="px-4 py-2 border-t border-gray-100">
                                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>My Business</span>
                              </div>
                              <Link to="/business/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors">
                                <Building2 size={18} style={{ color: '#FF7A1A' }} />
                                <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Business Dashboard</span>
                              </Link>
                            </>
                          )}
                          
                          {/* Venue Partner Section */}
                          {isVenuePartner && (
                            <>
                              <div className="px-4 py-2 border-t border-gray-100">
                                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>My Venue</span>
                              </div>
                              <Link to="/venue/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors">
                                <Store size={18} style={{ color: '#6F5BEB' }} />
                                <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Venue Partner Dashboard</span>
                              </Link>
                            </>
                          )}
                          
                          {/* Consumer Section */}
                          <div className="px-4 py-2 border-t border-gray-100">
                            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>My Deals</span>
                          </div>
                          <Link to="/deal-wallet" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors">
                            <Wallet size={18} style={{ color: '#FF7A1A' }} />
                            <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deal Wallet</span>
                          </Link>
                          {flag('REWARDS') && (
                          <Link to="/rewards" onClick={() => setShowUserMenu(false)} className="flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors">
                            <Trophy size={18} style={{ color: '#8b5cf6' }} />
                            <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Taps Rewards</span>
                            {tapsBalance > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-purple-500 text-white text-xs font-bold rounded-full">
                                {tapsBalance}
                              </span>
                            )}
                          </Link>
                          )}
                          
                          {/* Logout */}
                          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 border-t border-gray-100 transition-colors">
                            <LogOut size={18} style={{ color: '#EF4444' }} />
                            <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#EF4444' }}>Log Out</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Mobile: Rewards Button + Menu Button */}
              <div className="md:hidden flex items-center gap-2">
                {/* Mobile Rewards Dropdown */}
                <div className="relative" ref={mobileRewardsRef}>
                  <motion.button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={showRewardsMenu}
                    aria-label="Deals menu"
                    onClick={() => setShowRewardsMenu(!showRewardsMenu)}
                    className="px-3 min-h-[44px] min-w-[44px] rounded-full text-sm font-semibold whitespace-nowrap font-body flex items-center justify-center gap-1"
                    style={{
                      color: '#FFFFFF',
                      backgroundColor: '#FF7A1A',
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Gift size={18} aria-hidden="true" />
                    <ChevronDown size={14} aria-hidden="true" className={`transform transition-transform ${showRewardsMenu ? 'rotate-180' : ''}`} />
                  </motion.button>
                  
                  <AnimatePresence>
                    {showRewardsMenu && (
                      <motion.div 
                        className="absolute right-0 mt-2 w-56 rounded-2xl shadow-2xl overflow-hidden z-50"
                        style={{ backgroundColor: '#FFFFFF', border: '1px solid #FFE4CC' }}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {/* Deals Near You */}
                        <Link 
                          to="/local-deals-near-me" 
                          onClick={() => setShowRewardsMenu(false)} 
                          className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors"
                        >
                          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                            <span className="text-sm">🎁</span>
                          </div>
                          <div>
                            <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deals Near You</span>
                            <p className="text-xs text-gray-500">Find local offers</p>
                          </div>
                        </Link>
                        
                        {/* Deal Wallet */}
                        {isAuthenticated ? (
                          <Link 
                            to="/deal-wallet" 
                            onClick={() => setShowRewardsMenu(false)} 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center">
                              <Wallet size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deal Wallet</span>
                              <p className="text-xs text-gray-500">Your saved deals</p>
                            </div>
                            {claimsCount > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                                {claimsCount}
                              </span>
                            )}
                          </Link>
                        ) : (
                          <button 
                            onClick={() => {
                              setShowRewardsMenu(false);
                              openAuthModal('login');
                            }} 
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors text-left"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center">
                              <Wallet size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Deal Wallet</span>
                              <p className="text-xs text-gray-500">Sign in to view</p>
                            </div>
                          </button>
                        )}
                        
                        {/* Taps Rewards */}
                        {flag('REWARDS') && (isAuthenticated ? (
                          <Link 
                            to="/rewards" 
                            onClick={() => setShowRewardsMenu(false)} 
                            className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                              <Trophy size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Taps Rewards</span>
                              <p className="text-xs text-gray-500">Earn & redeem points</p>
                            </div>
                            {tapsBalance > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-purple-500 text-white text-xs font-bold rounded-full">
                                {tapsBalance}
                              </span>
                            )}
                          </Link>
                        ) : (
                          <button 
                            onClick={() => {
                              setShowRewardsMenu(false);
                              openAuthModal('login');
                            }} 
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 border-t border-orange-100 transition-colors text-left"
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                              <Trophy size={14} className="text-white" />
                            </div>
                            <div>
                              <span className="text-sm font-semibold" style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>Taps Rewards</span>
                              <p className="text-xs text-gray-500">Sign in to earn</p>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Menu Button */}
                <motion.button
                  type="button"
                  aria-label="Open menu"
                  aria-expanded={mobileMenuOpen}
                  className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  onClick={() => setMobileMenuOpen(true)}
                  whileTap={{ scale: 0.9 }}
                >
                  <Menu size={24} color="#111827" aria-hidden="true" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* New Role-Aware Mobile Navigation */}
      <MobileNav 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
      />
    </>
  );
};

export default HeaderLegacy;
